/**
 * Utility functions for determining pattern tier requirements
 */

/**
 * Checks if a pattern uses features that are locked at the given subscription tier
 * @param {Object[]} measures - The pattern's measures
 * @param {string} subscriptionTier - User's subscription tier ("free", "basic", or "pro")
 * @param {boolean} useTempoChanges - Whether pattern uses tempo changes
 * @param {boolean} modulateOnLoop - Whether pattern uses modulate on loop
 * @returns {Object} Result with locked status and required tier info
 */
export function patternUsesLockedFeatures(
    measures,
    subscriptionTier,
    useTempoChanges,
    modulateOnLoop
  ) {
    // FREE TIER can access basic features
    // BASIC TIER can access basic and advanced features
    // PRO TIER can access all features
  
    // Check for PRO-only features if user is not on Pro tier
    if (subscriptionTier !== "pro") {
      // Check pattern-level Pro features
      if (modulateOnLoop) {
        return { locked: true, requiredTier: "pro", feature: "Modulate on Loop" };
      }
  
      // Check measure-level Pro features
      for (const measure of measures) {
        // Check for beat-level Pro features
        for (const beatSound of measure.beatSounds || []) {
          // Quarter tones feature
          if (beatSound.useQuarterTones) {
            return {
              locked: true,
              requiredTier: "pro",
              feature: "Quarter Tones",
            };
          }
  
          // Check for harmony features (if they're Pro-only)
          if (
            beatSound.hasHarmony &&
            beatSound.selectedHarmony?.harmonyType === "complex"
          ) {
            return {
              locked: true,
              requiredTier: "pro",
              feature: "Complex Harmony",
            };
          }
  
          // Check for specific subdivision features
          for (const sub of beatSound.subdivisions || []) {
            // Advanced subdivision features
            if (sub.useDynamicAccents) {
              return {
                locked: true,
                requiredTier: "pro",
                feature: "Dynamic Accents",
              };
            }
          }
        }
  
        // Check for tuplets
        for (const tuplet of measure.tuplets || []) {
          // Nested tuplets are Pro-only
          if (tuplet.nestedTuplets && tuplet.nestedTuplets.length > 0) {
            return {
              locked: true,
              requiredTier: "pro",
              feature: "Nested Tuplets",
            };
          }
  
          // Complex tuplet ratios are Pro-only
          if (
            tuplet.ratio &&
            !["2:3", "3:2", "3:4", "4:3"].includes(tuplet.ratio)
          ) {
            return {
              locked: true,
              requiredTier: "pro",
              feature: "Complex Tuplet Ratios",
            };
          }
        }
      }
    }
  
    // Check for BASIC-only features if user is on Free tier
    if (subscriptionTier === "free") {
      // Pattern-level Basic features
      if (useTempoChanges) {
        return { locked: true, requiredTier: "basic", feature: "Tempo Changes" };
      }
  
      // Check measure-level Basic features
      for (const measure of measures) {
        // Metric modulation
        if (measure.hasModulation) {
          return {
            locked: true,
            requiredTier: "basic",
            feature: "Metric Modulation",
          };
        }
  
        // Any tuplets (all tuplets might be Basic tier or higher)
        if (measure.tuplets && measure.tuplets.length > 0) {
          return { locked: true, requiredTier: "basic", feature: "Tuplets" };
        }
  
        // Check beat sounds for Basic features
        for (const beatSound of measure.beatSounds || []) {
          // Mute subdivisions feature
          if (
            beatSound.subdivisions &&
            beatSound.subdivisions.some(
              (sub) => sub.mutedSubs && sub.mutedSubs.length > 0
            )
          ) {
            return {
              locked: true,
              requiredTier: "basic",
              feature: "Mute Subdivisions",
            };
          }
  
          // Custom sound features
          if (
            beatSound.soundId &&
            beatSound.soundId !== "default" &&
            beatSound.soundId !== "clicker"
          ) {
            return {
              locked: true,
              requiredTier: "basic",
              feature: "Custom Sounds",
            };
          }
  
          // Basic harmony features
          if (beatSound.hasHarmony) {
            return { locked: true, requiredTier: "basic", feature: "Harmony" };
          }
        }
      }
    }
  
    // No locked features found
    return { locked: false };
  }
  
  /**
   * Determines the subscription tier required for a pattern
   * @param {Object} pattern - The pattern object
   * @returns {string} Required tier ("free", "basic", or "pro")
   */
  export function determinePatternTier(pattern) {
    // Default to "free" if we can't determine
    if (!pattern || !pattern.measures) return "free";
  
    // Check if basic tier features are used
    const basicCheck = patternUsesLockedFeatures(
      pattern.measures,
      "free",
      pattern.useTempoChanges || false,
      pattern.modulateOnLoop || false
    );
  
    if (basicCheck.locked && basicCheck.requiredTier === "basic") {
      // Check if pro tier features are used
      const proCheck = patternUsesLockedFeatures(
        pattern.measures,
        "basic",
        pattern.useTempoChanges || false,
        pattern.modulateOnLoop || false
      );
  
      if (proCheck.locked) {
        return "pro";
      }
      return "basic";
    } else if (basicCheck.locked && basicCheck.requiredTier === "pro") {
      return "pro";
    }
  
    return "free";
  }
  
  /**
   * React component to render a tier badge
   */
  export function TierBadge({ tier }) {
    const tierColors = {
      free: "bg-emerald-100 text-emerald-800",
      basic: "bg-orange-100 text-orange-800",
      pro: "bg-red-100 text-red-800",
    };
  
    const displayTier = tier.charAt(0).toUpperCase() + tier.slice(1);
  
    return (
      <span
        className={`text-xs font-medium px-2 py-1 rounded-full ${
          tierColors[tier] || tierColors.free
        }`}
      >
        {displayTier}
      </span>
    );
  }
  